import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import RiddleBank from "../Files/Riddles.json";
import { ProblemData, RiddleDecoderEntry } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { byAnswer, poolSettings } from "./ColorByNumberGenerator.ts";

/**
 * A riddle decoder (ADR 0088 §3): a riddle's punchline spelled out one letter
 * at a time, each letter under a problem whose answer the decoder keys to it.
 * A wrong answer leaves a blank or a letter that does not read, which is the
 * self-check.
 *
 * Built on the same pool as color by number (ADR 0058 §3): the sheet's own
 * settings, asked back of the arithmetic generator as plain facts, grouped by
 * exact answer.
 */

export interface Riddle {
    question: string;
    answer: string;
}

export const RIDDLES: Riddle[] = RiddleBank;

/** The most problems a riddle page holds: the longest punchline in the bank. */
export const RIDDLE_MAX_LETTERS = Math.max(...RIDDLES.map((riddle) => lettersOf(riddle.answer).length));

/** Decoys are decoder rows for letters the punchline does not use. */
export const MAX_DECOYS = 4;
/** A riddle needs at least this many spare answers, or the decoder gives its letters away. */
export const MIN_DECOYS = 2;

/** The punchline's letters in order, spaces dropped: one per problem. */
export function lettersOf(answer: string): string[] {
    return answer.split("").filter((char) => char >= "A" && char <= "Z");
}

export function distinctLetters(answer: string): string[] {
    return Array.from(new Set(lettersOf(answer)));
}

/** The riddles whose letters the pool has enough different answers to key, with spare. */
export function riddlesFitting(distinctAnswers: number): Riddle[] {
    return RIDDLES.filter((riddle) => distinctLetters(riddle.answer).length + MIN_DECOYS <= distinctAnswers);
}

/**
 * The fewest different answers a riddle in the bank can be keyed with. Read by
 * the page-count sweep and the tests, so neither restates it.
 */
export function fewestAnswersNeeded(): number {
    return Math.min(...RIDDLES.map((riddle) => distinctLetters(riddle.answer).length)) + MIN_DECOYS;
}

/**
 * Gives each of the punchline's letters its own answer. A letter that is used
 * several times takes an answer with several different facts where the pool has
 * one, so its problems are not the same fact repeated; the answer is otherwise
 * drawn at random, so the decoder does not always key the same numbers.
 */
function assignLetters(
    letters: string[],
    uses: Map<string, number>,
    groups: Map<number, ProblemData[]>
): Map<string, number> | null {
    const remaining = new Set(groups.keys());
    const assigned = new Map<string, number>();
    // Most-used letters choose first; ties are shuffled so pages differ.
    for (const letter of shuffle(letters).sort((a, b) => uses.get(b)! - uses.get(a)!)) {
        const needed = uses.get(letter)!;
        const open = [...remaining];
        if (open.length === 0) {
            return null;
        }
        const roomy = open.filter((answer) => groups.get(answer)!.length >= needed);
        const widest = Math.max(...open.map((answer) => groups.get(answer)!.length));
        const pickFrom = roomy.length > 0 ? roomy : open.filter((answer) => groups.get(answer)!.length === widest);
        const answer = shuffle(pickFrom)[0];
        assigned.set(letter, answer);
        remaining.delete(answer);
    }
    return assigned;
}

/** One riddle's page: a problem for each letter of the punchline, in order. */
export function buildRiddlePage(riddle: Riddle, pool: ProblemData[]): ProblemData[] | null {
    const groups = byAnswer(pool);
    const letters = distinctLetters(riddle.answer);
    if (letters.length + MIN_DECOYS > groups.size) {
        return null;
    }
    const sequence = lettersOf(riddle.answer);
    const uses = new Map<string, number>();
    for (const letter of sequence) {
        uses.set(letter, (uses.get(letter) ?? 0) + 1);
    }

    const assigned = assignLetters(letters, uses, groups);
    if (!assigned) {
        return null;
    }

    // Decoys: answers the punchline does not use, each keyed to a letter it does not use.
    const spare = shuffle([...groups.keys()].filter((answer) => ![...assigned.values()].includes(answer)));
    const spareLetters = shuffle("ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").filter((letter) => !letters.includes(letter)));
    const decoys = Math.min(MAX_DECOYS, spare.length, spareLetters.length);
    const decoder: RiddleDecoderEntry[] = [
        ...letters.map((letter) => ({ answer: assigned.get(letter)!, letter })),
        ...spare.slice(0, decoys).map((answer, i) => ({ answer, letter: spareLetters[i] })),
    ].sort((a, b) => a.answer - b.answer);

    // Each answer's facts, dealt one at a time so a repeated letter gets different facts.
    const decks = new Map<number, ProblemData[]>();
    const dealt = new Map<number, number>();
    for (const answer of assigned.values()) {
        decks.set(answer, shuffle(groups.get(answer)!));
        dealt.set(answer, 0);
    }

    return sequence.map((letter, position) => {
        const answer = assigned.get(letter)!;
        const deck = decks.get(answer)!;
        const n = dealt.get(answer)!;
        dealt.set(answer, n + 1);
        const fact = deck[n % deck.length];
        const problem: ProblemData = { ...fact, key: position, riddle: { position, letter } };
        if (position === 0) {
            problem.riddleSheet = { question: riddle.question, punchline: riddle.answer, decoder };
        }
        return problem;
    });
}

type Generate = (settings: ISettingsProps) => ProblemData[][] | null;

/**
 * Riddle pages (ADR 0088 §3), one riddle a page. Riddles are drawn without
 * repeating inside one print, until the bank that fits is used up. Null when
 * the settings give too few different answers for any riddle in the bank.
 */
export function generateRiddlePages(settings: ISettingsProps, generate: Generate): ProblemData[][] | null {
    const pages: ProblemData[][] = [];
    const used = new Set<string>();
    for (let i = 0; i < settings.NumberOfPages; i++) {
        const pool = generate(poolSettings(settings))?.flat() ?? [];
        const fitting = riddlesFitting(byAnswer(pool).size);
        if (fitting.length === 0) {
            return null;
        }
        const fresh = fitting.filter((riddle) => !used.has(riddle.answer));
        const choices = fresh.length > 0 ? fresh : fitting;
        const riddle = choices[getRandomInt(0, choices.length - 1)];
        used.add(riddle.answer);
        const page = buildRiddlePage(riddle, pool);
        if (!page) {
            return null;
        }
        pages.push(page);
    }
    return pages;
}
